<script setup>
import { ref, computed, nextTick, inject, onMounted } from 'vue'
import Papa from 'papaparse'
import bootstrap from 'bootstrap/dist/js/bootstrap.bundle'
import { RequestService } from '../shared/services/RequestService'
import { EmailUtilsService } from '../shared/services/EmailUtilsService'
import { useEmployerStore } from '../shared/stores/EmployerStore'
import { storeToRefs } from 'pinia'
import { handleErrors } from '../shared/utils/errorHandler'
import { getAuth } from 'firebase/auth'
import { splitOwnerCell } from '../shared/utils/accountOwnersHelpers'

const { post } = RequestService()
const { getEmailTemplate, getEmailRecipient } = EmailUtilsService()
const functionsBaseUrl = inject('functionsBaseUrl')
const employerStore = useEmployerStore()
const { userData } = storeToRefs(employerStore)
const submitting = ref(false)
const submitSuccess = ref(false)
const submitError = ref(false)
const customMessage = ref('')
const csvFile = ref(null)
const loading = ref(false)
const tableData = ref([])
const fileInputRef = ref(null)
const showCsvHints = ref(false)

// CSV column definitions for the table
const csvColumns = [
  'Account Owner',
  'Related Account/Employee Name',
  'EIN',
  'Plan Year Start Date',
  'Primary Contact Name',
  'Primary Contact Email 1',
  'Primary Contact Email 2',
  'Control Group Y/N',
  'ERISA Plan Y/N',
  'Employees on Plan',
  'Funding Type',
  'Group Medical? Y/N',
  'Dental? Y/N',
  'Vision? Y/N',
  'Voluntary Supplemental Life Y/N',
  'Voluntary Supplemental Life for Spouse/Dependents Y/N?',
  'STD? Y/N',
  'LTD? Y/N',
  'FSA? Y/N',
  'HRA? Y/N',
  'ICHRA? Y/N',
  'Wellness Program? Y/N',
  'Tobacco Surcharge? Y/N',
  'Worksite Benefits? Y/N',
  'MEC Plan? Y/N',
  'Dependent Care FSA? Y/N',
  'HSA? Y/N',
  'Employer-paid Group Term Life? Y/N'
]

const fundingTypeOptions = [
  'Fully Insured',
  'Self-Insured',
  'Level Funded',
  "Don't Know"
]

// Coverage columns that require at least one YES (from Control Group Y/N to the end)
const coverageColumns = [
  'Control Group Y/N',
  'ERISA Plan Y/N',
  'Employees on Plan',
  'Funding Type',
  'Group Medical? Y/N',
  'Dental? Y/N',
  'Vision? Y/N',
  'Voluntary Supplemental Life Y/N',
  'Voluntary Supplemental Life for Spouse/Dependents Y/N?',
  'STD? Y/N',
  'LTD? Y/N',
  'FSA? Y/N',
  'HRA? Y/N',
  'ICHRA? Y/N',
  'Wellness Program? Y/N',
  'Tobacco Surcharge? Y/N',
  'Worksite Benefits? Y/N',
  'MEC Plan? Y/N',
  'Dependent Care FSA? Y/N',
  'HSA? Y/N',
  'Employer-paid Group Term Life? Y/N'
]

// Header display mappings (split long names into multiple lines)
const headerMappings = {
  'Account Owner': 'Account\nOwner',
  'Related Account/Employee Name': 'Related Account/\nEmployee Name',
  'Plan Year Start Date': 'Plan Year\nStart Date',
  'Employees on Plan': 'Employees\non Plan',
  'Funding Type': 'Funding\nType',
  'Control Group Y/N': 'Control\nGroup Y/N',
  'ERISA Plan Y/N': 'ERISA\nPlan Y/N',
  'Group Medical? Y/N': 'Group\nMedical? Y/N',
  'Dental? Y/N': 'Dental?\nY/N',
  'Vision? Y/N': 'Vision?\nY/N',
  'Voluntary Supplemental Life Y/N': 'Vol. Supp.\nLife Y/N',
  'Voluntary Supplemental Life for Spouse/Dependents Y/N?': 'Vol. Life\nSpouse/Dep Y/N',
  'STD? Y/N': 'STD?\nY/N',
  'LTD? Y/N': 'LTD?\nY/N',
  'FSA? Y/N': 'FSA?\nY/N',
  'HRA? Y/N': 'HRA?\nY/N',
  'ICHRA? Y/N': 'ICHRA?\nY/N',
  'Wellness Program? Y/N': 'Wellness\nProgram? Y/N',
  'Tobacco Surcharge? Y/N': 'Tobacco\nSurcharge? Y/N',
  'Worksite Benefits? Y/N': 'Worksite\nBenefits? Y/N',
  'MEC Plan? Y/N': 'MEC\nPlan? Y/N',
  'Dependent Care FSA? Y/N': 'Dep. Care\nFSA? Y/N',
  'HSA? Y/N': 'HSA?\nY/N',
  'Employer-paid Group Term Life? Y/N': 'Emp-paid\nGroup Life Y/N',
  'Primary Contact Name': 'Primary\nContact Name',
  'Primary Contact Email 1': 'Primary Contact\nEmail 1',
  'Primary Contact Email 2': 'Primary Contact\nEmail 2'
}
// Y/N columns only (for the at least one YES validation)
const yesNoColumns = coverageColumns.filter(col => {
  const lower = col.toLowerCase()
  return lower.includes('y/n') || lower.includes('yes/no')
})


// Fetch user data on mount if not already loaded
onMounted(async () => {
  if (!userData.value?.email) {
    try {
      const auth = getAuth()
      const token = await auth.currentUser?.getIdToken()
      const response = await fetch(`${functionsBaseUrl}/usersGateway/api/v1/users/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      if (response.ok) {
        const data = await response.json()
        if (data?.object) {
          userData.value = { ...userData.value, ...data.object }
        }
      }
    } catch (e) {
      handleErrors('Could not fetch user data:', e)
    }
  }
})

// Track which date cell is being edited (rowIndex)
const editingDateCell = ref(null)

// Format column header for display (split long names into multiple lines)
function formatColumnHeader(column) {
  return headerMappings[column] || column
}

// Format date as human-readable (e.g., "January 1, 2025")
function formatDateHuman(dateStr) {
  if (!dateStr) return ''
  const trimmed = String(dateStr).trim()
  const date = new Date(trimmed)
  if (isNaN(date.getTime())) return dateStr
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

// Auto-format date to MM/DD/YYYY with leading zeros using native JS Date
function formatDateWithLeadingZeros(dateStr) {
  if (!dateStr) return ''
  const trimmed = String(dateStr).trim()
  const date = new Date(trimmed)
  if (isNaN(date.getTime())) return dateStr
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const year = date.getFullYear()
  return `${month}/${day}/${year}`
}

// Validate a single cell and return error message if invalid
function validateCell(column, value) {
  const trimmedValue = value ? String(value).trim() : ''
  
  // Account Owner (optional; up to 3 valid emails separated by ';' or ',')
  if (column === 'Account Owner') {
    if (trimmedValue) {
      const emailRegex = /^[\w-.]+@([\w-]+\.)+[\w-]{2,}$/
      const emails = splitOwnerCell(trimmedValue)
      if (emails.length > 3) {
        return 'A maximum of 3 Account Owners is allowed'
      }
      if (!emails.every(e => emailRegex.test(e))) {
        return 'Must contain valid email addresses'
      }
    }
    return null
  }
  
  // Required field: Related Account/Employee Name
  if (column === 'Related Account/Employee Name') {
    if (!trimmedValue) {
      return 'Employer name is required'
    }
  }
  
  // EIN validation (XX-XXXXXXX format)
  if (column === 'EIN' && trimmedValue) {
    const einRegex = /^\d{2}-\d{7}$/
    if (!einRegex.test(trimmedValue)) {
      return 'Must be in XX-XXXXXXX format (e.g., 12-3456789)'
    }
  }
  
  // Required field: Plan Year Start Date (MM/DD/YYYY format)
  if (column === 'Plan Year Start Date') {
    if (!trimmedValue) {
      return 'Plan Year Start Date is required'
    }
    const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/
    if (!dateRegex.test(trimmedValue)) {
      return 'Must be in MM/DD/YYYY format (e.g., 01/01/2026)'
    }
    const date = new Date(trimmedValue)
    if (isNaN(date.getTime())) {
      return 'Invalid date'
    }
  }
  
  // Employees on Plan - must be a valid number if present
  if (column === 'Employees on Plan' && trimmedValue) {
    if (!/^\d+$/.test(trimmedValue)) {
      return 'Must be a valid number'
    }
  }
  
  // Funding Type - optional, must be one of the valid options if provided
  if (column === 'Funding Type' && trimmedValue) {
    const isValid = fundingTypeOptions.some(
      opt => opt.toLowerCase() === trimmedValue.toLowerCase()
    )
    if (!isValid) {
      return `Must be one of: ${fundingTypeOptions.join(', ')}`
    }
  }
  
  // Boolean Y/N fields validation (optional - can be blank)
  const keyLower = column.toLowerCase()
  if (keyLower.includes('y/n') || keyLower.includes('yes/no')) {
    if (trimmedValue) {
      const upperVal = trimmedValue.toUpperCase()
      if (upperVal !== 'Y' && upperVal !== 'N') {
        return 'Must be "Y", "N", or blank'
      }
    }
  }
  
  // Email validation for contact emails - allows any TLD (.com, .enterprise, etc.)
  if ((column === 'Primary Contact Email 1' || column === 'Primary Contact Email 2') && trimmedValue) {
    const emailRegex = /^[\w-.]+@([\w-]+\.)+[\w-]{2,}$/
    if (!emailRegex.test(trimmedValue)) {
      return 'Invalid email format'
    }
  }
  
  return null // No error
}

// Check if at least one Y/N column has Y
function hasAtLeastOneYesNoYes(row) {
  return yesNoColumns.some(col => {
    const val = row[col] ? String(row[col]).trim().toUpperCase() : ''
    return val === 'Y'
  })
}

// Check if date day is not 1 (for warning)
function isDateDayNotFirst(dateStr) {
  if (!dateStr) return false
  const date = new Date(String(dateStr).trim())
  if (isNaN(date.getTime())) return false
  return date.getDate() !== 1
}

// Validate a row and return object with errors per column
function validateRow(row) {
  const errors = {}
  csvColumns.forEach(column => {
    const error = validateCell(column, row[column])
    if (error) {
      errors[column] = error
    }
  })
  
  // Row-level validation: at least one Y/N column must be Y
  if (!hasAtLeastOneYesNoYes(row)) {
    errors['_coverageRequired'] = 'At least one coverage option must be "Y"'
  }
  
  // Warning: date day is not 1
  if (isDateDayNotFirst(row['Plan Year Start Date'])) {
    errors['_dateWarning'] = 'Plan year typically starts on the 1st of the month'
  }
  
  return errors
}

const warningKeys = ['_dateWarning']

function isWarningKey(key) {
  return warningKeys.includes(key)
}

function rowHasBlockingErrors(errors) {
  return Object.keys(errors).some(key => !isWarningKey(key))
}

// Update cell value and revalidate
function updateCellValue(rowIndex, column, newValue) {
  const row = tableData.value[rowIndex]
  if (row) {
    row.data[column] = newValue
    // Revalidate the entire row
    const isEmptyRow = Object.values(row.data).every(val => !val || String(val).trim() === '')
    row.errors = isEmptyRow ? { _row: 'Empty row' } : validateRow(row.data)
    row.hasErrors = rowHasBlockingErrors(row.errors)
  }
}

// Process CSV file and populate table with validation
const processCSV = file => {
  return new Promise(resolve => {
    Papa.parse(file, {
      header: true,
      complete: results => {
        const data = results.data
        // Ignore trailing empty rows
        const lastNonEmptyIdx = data.reduce((last, r, i) => {
          const emptyRow = Object.values(r).every(val => !val || String(val).trim() === '')
          return !emptyRow ? i : last
        }, -1)
        const trimmedData = data.slice(0, lastNonEmptyIdx + 1)
        
        // Process each row with validation
        const processedRows = trimmedData.map((row, idx) => {
          // Auto-format Plan Year Start Date with leading zeros
          if (row['Plan Year Start Date']) {
            row['Plan Year Start Date'] = formatDateWithLeadingZeros(row['Plan Year Start Date'])
          }
          
          const isEmptyRow = Object.values(row).every(val => !val || String(val).trim() === '')
          const errors = isEmptyRow ? { _row: 'Empty row' } : validateRow(row)
          
          return {
            rowNumber: idx + 2, // CSV row number (1-indexed + header)
            data: row,
            errors,
            hasErrors: rowHasBlockingErrors(errors)
          }
        })
        
        resolve(processedRows)
      }
    })
  })
}

// Handle file upload
const handleFileUpload = async event => {
  const file = event.target.files[0]
  if (file) {
    csvFile.value = file
    loading.value = true
    try {
      const rows = await processCSV(file)
      tableData.value = rows
    } catch (error) {
      handleErrors('Error processing CSV file:', error)
      tableData.value = []
    } finally {
      loading.value = false
    }
  }
}

// Check if all rows are valid
const allRowsValid = computed(() => {
  if (tableData.value.length === 0) return false
  return tableData.value.every(row => !row.hasErrors)
})

// Count of rows with errors
const errorCount = computed(() => {
  return tableData.value.filter(row => row.hasErrors).length
})

// Generate error list for display (individual errors)
const errorList = computed(() => {
  const allErrors = []
  tableData.value.forEach(row => {
    if (row.hasErrors) {
      Object.keys(row.errors).forEach(col => {
        // Skip internal keys except _coverageRequired
        if (col.startsWith('_') && col !== '_coverageRequired') return
        allErrors.push({
          rowNumber: row.rowNumber,
          column: col === '_coverageRequired' ? 'Coverage Options' : col,
          message: row.errors[col]
        })
      })
    }
  })
  return allErrors
})

 const warningList = computed(() => {
  const allWarnings = []
  tableData.value.forEach(row => {
    Object.keys(row.errors || {}).forEach(col => {
      if (!isWarningKey(col)) return
      allWarnings.push({
        rowNumber: row.rowNumber,
        column: col === '_dateWarning' ? 'Plan Year Start Date' : col,
        message: row.errors[col]
      })
    })
  })
  return allWarnings
 })

// Format error list as text for copying
const errorListText = computed(() => {
  return errorList.value.map(err => 
    `Row ${err.rowNumber} / ${err.column} / ${err.message}`
  ).join('\n')
})

// Copy feedback state
const copySuccess = ref(false)

// Copy error list to clipboard
const copyErrorList = async () => {
  if (!errorListText.value) return
  try {
    await navigator.clipboard.writeText(errorListText.value)
    copySuccess.value = true
    setTimeout(() => { copySuccess.value = false }, 2000)
  } catch (err) {
    console.error('Failed to copy:', err)
  }
}

// Download CSV template
const downloadTemplate = () => {
  // Template row values by column index:
  // 0: Account Owner, 1: Related Account/Employee Name, 2: EIN, 3: Plan Year Start Date,
  // 4: Primary Contact Name, 5: Primary Contact Email 1, 6: Primary Contact Email 2,
  // 7: Control Group Y/N, 8: ERISA Plan Y/N, 9: Employees on Plan, 10: Funding Type, 11+: Y/N columns
  const templateRow = [
    'owner@agency.com',       // 0: Account Owner (optional)
    'Test Employer Name',     // 1
    '12-3456789',             // 2
    '01/01/2025',             // 3
    'John Doe',               // 4
    'test@email.com',         // 5
    'test2@email.com',        // 6
    'N',                      // 7
    'N',                      // 8
    '4',                      // 9
    'Fully Insured',          // 10
    'N', 'N', 'N', 'N', 'N',  // 11-15
    'N', 'N', 'N', 'N', 'N',  // 16-20
    'N', 'N', 'N', 'N', 'N',  // 21-25
    'N', 'N'                  // 26-27
  ]
  const csvContent = [
    csvColumns.join(','),
    templateRow.map(val => `"${val}"`).join(',')
  ].join('\n')
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.setAttribute('download', 'employers_template.csv')
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

// Generate CSV content from table data
const generateCSVContent = () => {
  const rows = tableData.value.map(row => {
    return csvColumns.map(col => {
      const val = row.data[col] || ''
      if (String(val).includes(',') || String(val).includes('"')) {
        return `"${String(val).replace(/"/g, '""')}"`
      }
      return val
    }).join(',')
  })
  return [csvColumns.join(','), ...rows].join('\n')
}

// Download current data as CSV (with errors for review)
const downloadCurrentCSV = () => {
  if (tableData.value.length === 0) return
  
  const csvContent = generateCSVContent()
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.setAttribute('download', 'employers_data.csv')
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

// Submit to support - sends email with CSV attachment
const submitToSupport = async () => {
  if (!allRowsValid.value || submitting.value) return
  
  try {
    submitting.value = true
    submitSuccess.value = false
    submitError.value = false
    
    const csvContent = generateCSVContent()
    const userEmail = userData.value?.email || 'unknown@email.com'
    const fullName = userData.value?.fullname || 'Unknown User'
    const agencyName = userData.value?.companyName || 'Unknown Agency'
    
    await post('sendEmail', `${functionsBaseUrl}/utilityGateway/api/v1/email/send`, {
      type: 'LOAD_EMPLOYERS_SUBMISSION',
      email: userEmail,
      data: {
        fullName: fullName,
        agencyName: agencyName,
        employerCount: tableData.value.length,
        csvContent: csvContent,
        fileName: `validated_employers_${new Date().toISOString().split('T')[0]}.csv`,
        submittedAt: new Date().toLocaleString('en-US', { dateStyle: 'long', timeStyle: 'short' }),
        customMessage: customMessage.value || ''
      },
      emailTemplate: await getEmailTemplate('LOAD_EMPLOYERS_SUBMISSION'),
      emailRecipient: await getEmailRecipient('LOAD_EMPLOYERS_SUBMISSION')
    })
    
    submitSuccess.value = true
  } catch (error) {
    handleErrors('Error submitting to support:', error)
    submitError.value = true
  } finally {
    submitting.value = false
  }
}

// Clear uploaded data
const clearData = () => {
  tableData.value = []
  csvFile.value = null
  if (fileInputRef.value) fileInputRef.value.value = ''
}

// Initialize Bootstrap tooltips
nextTick(() => {
  const triggers = document.querySelectorAll('[data-bs-toggle="tooltip"]')
  triggers.forEach(el => new bootstrap.Tooltip(el))
})
</script>

<template>
  <div class="main-container">
    <div class="row d-flex justify-content-center">
      <div class="col-12 text-center my-3">
        <h1 class="mt-1 text-primary">Check & Validate Employers</h1>
      </div>
    </div>

    <div class="row d-flex justify-content-center">
      <div class="col-md-11">
        <div class="card">
          <div class="card-header bg-light">
            <h5 class="card-title header-20 text-secondary"><strong>CSV Validation Tool</strong></h5>
          </div>
          <div class="card-body">
            <!-- Button Panel - Full Width -->
            <div class="row g-2 mb-4">
              <div class="col-12 col-md-6 col-lg-3">
                <input
                  ref="fileInputRef"
                  type="file"
                  class="d-none"
                  accept=".csv"
                  @change="handleFileUpload"
                  :disabled="loading"
                />
                <button 
                  class="btn btn-primary w-100"
                  @click="fileInputRef?.click()"
                  :disabled="loading"
                >
                  <i class="bi bi-upload me-2"></i>
                  Upload CSV
                </button>
              </div>
              
              <div class="col-12 col-md-6 col-lg-3">
                <button 
                  class="btn btn-outline-secondary w-100"
                  @click="downloadCurrentCSV"
                  :disabled="tableData.length === 0"
                >
                  <i class="bi bi-file-earmark-arrow-down me-2"></i>
                  Download CSV
                </button>
              </div>
              
              <div class="col-12 col-md-6 col-lg-3">
                <button 
                  class="btn btn-success w-100"
                  @click="submitToSupport"
                  :disabled="!allRowsValid || submitting"
                >
                  <span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status"></span>
                  <i v-else class="bi bi-send me-2"></i>
                  {{ submitting ? 'Submitting...' : 'Submit to Support' }}
                </button>
              </div>
              
              <div class="col-12 col-md-6 col-lg-3">
                <button 
                  class="btn btn-outline-secondary w-100"
                  @click="downloadTemplate"
                >
                  <i class="bi bi-download me-2"></i>
                  Download Template
                </button>
              </div>
            </div>
            
            <!-- Clear Button -->
            <div v-if="tableData.length > 0" class="mb-4">
              <button 
                class="btn btn-outline-danger"
                @click="clearData"
              >
                <i class="bi bi-x-circle me-2"></i>
                Clear Data
              </button>
            </div>

            <!-- Submit Success/Error Messages -->
            <div v-if="submitSuccess" class="alert alert-success d-flex align-items-center mb-4">
              <i class="bi bi-check-circle-fill me-2"></i>
              CSV submitted successfully! The support team will process it shortly.
            </div>
            <div v-if="submitError" class="alert alert-danger d-flex align-items-center mb-4">
              <i class="bi bi-x-circle-fill me-2"></i>
              Failed to submit CSV. Please try again or contact support.
            </div>

            <!-- Custom Message (unlocked when all validations pass) -->
            <div v-if="tableData.length > 0" class="mb-4">
              <label for="customMessage" class="form-label fw-semibold">
                <i class="bi bi-chat-left-text me-1"></i>
                Custom Message (Optional)
              </label>
              <textarea
                id="customMessage"
                v-model="customMessage"
                class="form-control"
                :disabled="!allRowsValid"
                :placeholder="allRowsValid ? 'Add any additional notes or instructions for the support team...' : 'Fix all validation errors to unlock this field'"
                rows="3"
              ></textarea>
              <small v-if="!allRowsValid" class="text-muted">
                <i class="bi bi-lock-fill me-1"></i>
                This field will be unlocked once all validation errors are resolved.
              </small>
            </div>

            <!-- CSV Hints Collapsible -->
            <div class="card border mb-4">
              <div class="card-header bg-light" @click="showCsvHints = !showCsvHints" style="cursor: pointer;">
                <h6 class="mb-0 text-body fw-semibold">
                  <span class="me-2">{{ showCsvHints ? '▼' : '▶' }}</span> CSV Columns & Validation Rules
                </h6>
              </div>
              <div v-show="showCsvHints" class="card-body p-2">
                <ul class="list-unstyled mb-0 small">
                  <li class="py-1"><code class="text-body fw-semibold">Account Owner</code> - Optional, valid email of a user in the agency. Leave empty for Unassigned</li>
                  <li class="py-1"><code class="text-body fw-semibold">Related Account/Employee Name</code> - <span class="text-danger">Required</span></li>
                  <li class="py-1"><code class="text-body fw-semibold">Plan Year Start Date</code> - <span class="text-danger">Required</span>, format: mm/dd/yyyy</li>
                  <li class="py-1"><code class="text-body fw-semibold">Employees on Plan</code> - Must be a valid number</li>
                  <li class="py-1"><code class="text-body fw-semibold">Funding Type</code> - Optional. Must be: Fully Insured, Self-Insured, Level Funded, Don't Know</li>
                  <li class="py-1"><code class="text-body fw-semibold">Y/N Fields</code> - Must be Yes/Y or No/N</li>
                </ul>
              </div>
            </div>

            <!-- Loading Spinner -->
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-2 text-muted">Processing CSV file...</p>
            </div>

            <!-- Validation Summary -->
            <div v-if="tableData.length > 0 && !loading" class="mb-3">
              <div v-if="allRowsValid" class="alert alert-success d-flex align-items-center">
                <i class="bi bi-check-circle-fill me-2"></i>
                All {{ tableData.length }} rows are valid and ready to submit.
              </div>
              <div v-else class="alert alert-warning">
                <div class="d-flex align-items-center justify-content-between mb-2">
                  <div>
                    <i class="bi bi-exclamation-triangle-fill me-2"></i>
                    <strong>{{ errorList.length }} validation errors</strong> in {{ errorCount }} of {{ tableData.length }} rows
                  </div>
                  <button 
                    class="btn btn-sm btn-outline-secondary"
                    @click="copyErrorList"
                    :disabled="errorList.length === 0"
                  >
                    <i class="bi" :class="copySuccess ? 'bi-check-lg text-success' : 'bi-clipboard'"></i>
                    {{ copySuccess ? 'Copied!' : 'Copy Errors' }}
                  </button>
                </div>
                <div class="error-list-container">
                  <ul class="error-list mb-0">
                    <li v-for="(err, idx) in errorList" :key="idx" class="error-list-item">
                      <strong>Row {{ err.rowNumber }}</strong> / <code>{{ err.column }}</code> / {{ err.message }}
                    </li>
                  </ul>
                </div>
              </div>

              <div v-if="warningList.length > 0" class="alert alert-warning">
                <div class="d-flex align-items-center justify-content-between mb-2">
                  <div>
                    <i class="bi bi-exclamation-triangle-fill me-2"></i>
                    <strong>{{ warningList.length }} warnings</strong>
                  </div>
                </div>
                <div class="error-list-container">
                  <ul class="error-list mb-0">
                    <li v-for="(warn, idx) in warningList" :key="idx" class="error-list-item">
                      <strong>Row {{ warn.rowNumber }}</strong> / <code>{{ warn.column }}</code> / {{ warn.message }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Data Table -->
            <div v-if="tableData.length > 0 && !loading" class="table-responsive">
              <table class="table table-bordered table-hover table-sm">
                <thead class="table-light sticky-top">
                  <tr>
                    <th class="text-center" style="min-width: 50px;">#</th>
                    <th 
                      v-for="column in csvColumns" 
                      :key="column"
                      class="text-center column-header"
                    >
                      <span v-html="formatColumnHeader(column).replace(/\n/g, '<br>')"></span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr 
                    v-for="row in tableData" 
                    :key="row.rowNumber"
                    :class="{ 'table-danger-light': row.hasErrors }"
                  >
                    <td class="text-center fw-bold">{{ row.rowNumber }}</td>
                    <td 
                      v-for="column in csvColumns" 
                      :key="column"
                      :class="{ 'cell-error': row.errors[column] }"
                    >
                      <div class="cell-content">
                        <!-- Date field: show human-readable format, switch to raw on focus -->
                        <template v-if="column === 'Plan Year Start Date'">
                          <input
                            v-if="editingDateCell === tableData.indexOf(row)"
                            type="text"
                            class="form-control form-control-sm cell-input"
                            :class="{ 'input-error': row.errors[column] }"
                            :value="row.data[column] || ''"
                            @input="updateCellValue(tableData.indexOf(row), column, $event.target.value)"
                            @blur="editingDateCell = null"
                            @keydown.enter="editingDateCell = null"
                            placeholder="mm/dd/yyyy"
                            autofocus
                          />
                          <div 
                            v-else
                            class="form-control form-control-sm cell-input date-display-cell"
                            :class="{ 'input-error': row.errors[column], 'input-warning': row.errors['_dateWarning'] && !row.errors[column] }"
                            @click="editingDateCell = tableData.indexOf(row)"
                          >
                            {{ row.data[column] ? formatDateHuman(row.data[column]) : '' }}
                            <span v-if="!row.data[column]" class="text-muted">mm/dd/yyyy</span>
                          </div>
                          <div v-if="row.errors['_dateWarning'] && !row.errors[column]" class="warning-message">
                            <i class="bi bi-exclamation-triangle-fill"></i>
                            {{ row.errors['_dateWarning'] }}
                          </div>
                        </template>
                        <!-- Other fields -->
                        <template v-else>
                          <input
                            type="text"
                            class="form-control form-control-sm cell-input"
                            :class="{ 'input-error': row.errors[column] }"
                            :value="row.data[column] || ''"
                            @input="updateCellValue(tableData.indexOf(row), column, $event.target.value)"
                            @keydown.enter="$event.target.blur()"
                          />
                        </template>
                        <div v-if="row.errors[column]" class="error-message">
                          <i class="bi bi-exclamation-circle"></i>
                          {{ row.errors[column] }}
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Empty State -->
            <div v-if="tableData.length === 0 && !loading" class="text-center py-5 text-muted">
              <i class="bi bi-file-earmark-spreadsheet" style="font-size: 3rem;"></i>
              <p class="mt-3">Upload a CSV file to validate employer data</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.main-container {
  background-color: #f5f5f5;
  min-height: 100vh;
  padding-bottom: 2rem;
}

.cell-error {
  background-color: #fff5f5 !important;
  border: 2px solid #dc3545 !important;
  box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.25);
}

.cell-content {
  position: relative;
}

.error-message {
  font-size: 0.75rem;
  color: #dc3545;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.table-danger-light {
  background-color: #fef2f2 !important;
}

.table-responsive {
  max-height: 60vh;
  overflow: auto;
}

.table {
  width: auto;
}

.table th {
  position: sticky;
  top: 0;
  background-color: #f8f9fa;
  z-index: 1;
  vertical-align: middle;
}

.column-header {
  white-space: nowrap;
  font-size: 0.75rem;
  line-height: 1.3;
  padding: 6px 4px !important;
}

.table td {
  vertical-align: top;
  font-size: 0.875rem;
  padding: 4px;
  white-space: nowrap;
  min-width: 90px;
}

.cell-input {
  width: 100%;
  min-width: 80px;
  font-size: 0.8rem;
}

.input-error {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.25) !important;
}

.date-display {
  font-size: 0.75rem;
  color: #198754;
  margin-top: 2px;
  font-style: italic;
}

.date-display-cell {
  cursor: pointer;
  background-color: #fff;
  min-height: 31px;
  display: flex;
  align-items: center;
  white-space: nowrap;
}

.date-display-cell:hover {
  background-color: #f8f9fa;
}

.input-warning {
  border-color: #ffc107 !important;
  box-shadow: 0 0 0 2px rgba(255, 193, 7, 0.25) !important;
}

.warning-message {
  font-size: 0.75rem;
  color: #856404;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.error-list-container {
  max-height: 200px;
  overflow-y: auto;
  background-color: #fff8e6;
  border-radius: 4px;
  padding: 8px 12px;
}

.error-list {
  list-style: none;
  padding-left: 0;
  margin: 0;
}

.error-list-item {
  padding: 4px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  font-size: 0.875rem;
}

.error-list-item:last-child {
  border-bottom: none;
}

.error-list-item code {
  background-color: rgba(0, 0, 0, 0.05);
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 0.8rem;
}
</style>

<style>
/* Global override: increase Bootstrap tooltip width */
.tooltip-inner {
  max-width: 400px !important;
  white-space: normal !important;
}
</style>
